/** @jsxImportSource @emotion/react */
import isempty from 'lodash.isempty';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import { teamDetailsPageAnalytics } from '../../analyticsConsts';
import {
  MATCH_AGEGROUP,
  SELECTED_RECOMMENDATIONS_TYPE_ID,
  TEAMS_TAB,
} from '../../const';
import { usePageHistory } from '../../context/pageHistory';
import { useSetContactFormDLContext } from '../../context/setContactFormDL';
import useClubAccreditation from '../../hooks/useClubAccreditation';
import useClubContactDetails from '../../hooks/useClubContactDetails';
import useElementHeight from '../../hooks/useElementHeight';
import useMediaQuery from '../../hooks/useMediaQuery';
import useScrollTop from '../../hooks/useScrollTop';
import useTeamPageAPI from '../../hooks/useTeamPageAPI';
import contactFormDataLayer from '../../utils/contactFormDataLayer';
import Button from '../Button';
import ClubDetailsContactDetails from '../ClubDetailsContactDetails';
import ClubDetailsHeroProvider from '../ClubDetailsHeroProvider';
import useStylesClub from '../ClubPage/styles';
import ContactFormModal from '../ContactFormModal';
import Footer from '../Footer';
import Loader from '../Loader';
import LocationDetailsMap from '../LocationDetailsMap';
import QuickLinks from '../QuickLinks';
import SocialMedia from '../SocialMedia';
import TeamInfo from '../TeamInfo';
import TeamStatus from '../TeamStatus';
import { venuePreviousPage } from '../VenuePage/conditionCheck';
import useStyles from './styles';

const { TEAM_NAME_HEADING, TEAM_DETAILS_WRAPPER } = teamDetailsPageAnalytics;

const TeamDetails = () => {
  const pageRef = useRef();
  useScrollTop(pageRef);
  const {
    teamPageData,
    isLoadingPageData,
    socialDataLinks,
    teamDetails,
    isLoading,
    selectedClubDetails,
    sourceLocation,
    preferences,
  } = useTeamPageAPI();
  const { [SELECTED_RECOMMENDATIONS_TYPE_ID]: selectedRecommendationType } =
    preferences || {};
  const { Category } = preferences;
  const { height: titleHeight, elementRef: titleElement } = useElementHeight();
  const styles = useStyles();
  const stylesClub = useStylesClub();
  const [backUrl, setBackUrl] = useState();
  const [menuTab, setMenuTab] = useState(null);
  const [contactFormVisible, setContactFormVisible] = useState(false);
  const { selectedTeamDetails } = selectedClubDetails || {};
  const wgsClubID =
    selectedClubDetails?.WgsClubId || selectedClubDetails?.accreditationid;
  // when we get the club details we want to fetch the accreditation and contact details
  const accreditation = useClubAccreditation(wgsClubID);
  const contactDetails = useClubContactDetails(wgsClubID);
  const isDesktop = useMediaQuery('screen and (min-width: 768px)');
  const previousUrl = localStorage.getItem('pathInfomation');
  const { previousPage } = usePageHistory();
  const { contactFormDL } = useSetContactFormDLContext();
  const location = useLocation();
  let currentPage;
  switch (true) {
    case location.pathname === '/':
      currentPage = 'homepage';
      break;
    case location.pathname.startsWith('/club'):
      currentPage = 'clubpage';
      break;
    case location.pathname.startsWith('/team'):
      currentPage = 'teampage';
      break;
    default:
      currentPage = 'other';
  }

  useEffect(() => {
    if (!previousPage && !previousUrl) {
      return;
    }
    setBackUrl(
      previousPage
        ? previousPage
        : previousUrl
        ? previousUrl
        : '/recommendation'
    );
  }, [previousPage, previousUrl]);
  const teamPage = true;
  const previousPageName = venuePreviousPage(previousUrl, teamPage);

  // based on condition modifying isHidden flag in tabs array
  useEffect(() => {
    TEAMS_TAB[1].isHidden = !(
      (!isempty(teamDetails?.lastresults) ||
        !isempty(teamDetails?.standingspositions)) &&
      MATCH_AGEGROUP.indexOf(teamDetails?.agegroup) === -1
    );
    setMenuTab(TEAMS_TAB);
  }, [teamDetails, menuTab, isLoading]);

  if (isLoadingPageData) {
    return <Loader message="Loading team info" />;
  }

  return (
    <div
      css={styles.base(isDesktop, isLoading)}
      ref={pageRef}
      id="main-content"
      tabIndex={-1}
    >
      <section>
        <QuickLinks statsHidden={TEAMS_TAB[1].isHidden} />
        {teamPageData && (
          <ClubDetailsHeroProvider
            title={teamDetails?.title}
            address={teamDetails?.address}
            distance={teamDetails?.distance}
            latitude={teamDetails?.latitude}
            longitude={teamDetails?.longitude}
            county={teamDetails?.county}
            accreditation={accreditation}
            css={stylesClub.hero}
            type={'club'}
            textSize={3}
            ref={titleElement}
            titleHeight={titleHeight}
            hasPreferences={!!selectedRecommendationType}
            analyticsId={TEAM_NAME_HEADING}
            isInternalLink="true"
            previousPageData={{ name: previousPageName, backUrl: backUrl }}
            backupUrl={backUrl}
            sourceLoction={sourceLocation}
            Category={Category}
            isTeamPage="true"
            isDesktop={isDesktop}
            selectedRecommendationType={selectedRecommendationType}
            {...teamPageData.hero}
          />
        )}
      </section>
      <section
        id={TEAM_DETAILS_WRAPPER}
        css={styles.wrapper(titleHeight, !!selectedRecommendationType)}
      >
        <div css={stylesClub.card}>
          {isLoading && (
            <Loader css={stylesClub.loader} message="Loading Team Details" />
          )}
          {!isLoading && teamDetails && menuTab && (
            <Fragment>
              <div css={styles.pageView}>
                <div css={styles.pageViewRight}></div>
                <div css={styles.pageViewLeft}>
                  <section
                    ref={React.createRef()}
                    className="aboutDetails"
                    css={stylesClub.navContent(currentPage)}
                  >
                    {teamDetails && (
                      <Fragment>
                        <TeamInfo
                          contact={contactDetails}
                          teamDetails={teamDetails}
                          pageData={teamPageData}
                          county={teamDetails?.county}
                          accreditation={accreditation}
                          onClick={() => setContactFormVisible(true)}
                        />
                      </Fragment>
                    )}
                  </section>
                  {(!isempty(teamDetails?.lastresults) ||
                    !isempty(teamDetails?.standingspositions)) &&
                    MATCH_AGEGROUP.indexOf(teamDetails?.agegroup) === -1 && (
                      <section>
                        <TeamStatus
                          teamDetails={teamDetails}
                          {...teamDetails}
                        />
                      </section>
                    )}
                  <section ref={React.createRef()} css={stylesClub.navContent}>
                    {teamDetails && (
                      <ClubDetailsContactDetails
                        contactDetails={contactDetails}
                      />
                    )}
                    {socialDataLinks && (
                      <SocialMedia
                        title={selectedClubDetails?.clubName}
                        socialLinks={socialDataLinks}
                        isClubDetailPage={true}
                      ></SocialMedia>
                    )}
                    <Button
                      css={styles.contactForm}
                      aria-label={`Contact Form`}
                      onClick={() => {
                        setContactFormVisible(true);
                        contactFormDataLayer({
                          ...contactFormDL,
                          eventLabel: 'FF Club Contact Form CTA - Contact Form',
                          link_text: 'contact form',
                          event_name: 'cta_primary_click',
                        });
                      }}
                    >
                      Contact Via Our Contact form
                    </Button>
                    {teamDetails && (
                      <LocationDetailsMap
                        {...teamDetails}
                        titleHeader="Club Contact"
                        subTitle="For information about a specific team, please get in touch with the club contact."
                        locationDetails={{
                          latitude: teamDetails?.latitude,
                          longitude: teamDetails?.longitude,
                          title: 'New Club',
                        }}
                        contact={contactDetails}
                        sourceLoction={sourceLocation}
                        onClick={() => setContactFormVisible(true)}
                      />
                    )}
                  </section>
                </div>
              </div>
            </Fragment>
          )}
        </div>
      </section>
      {contactFormVisible && (
        <ContactFormModal
          onClose={() => {
            setContactFormVisible(false);
          }}
          isVisible={contactFormVisible}
          clubDetails={selectedClubDetails}
          isClub={false}
          selectedTeamData={selectedTeamDetails}
        />
      )}
      {<Footer />}
    </div>
  );
};

export default TeamDetails;
